import type { AvexaClient } from '@avexa/api'

/**
 * The one Supabase client the public website uses.
 *
 * Built by `createAvexaClient` from `@avexa/api` (Docs/03, "Data-access layer")
 * so the website, the admin panel and the mobile app cannot drift on session
 * handling — and so that the key-safety check in that factory runs here too:
 * `.env` is committed in this repository, the anon and service-role keys sit
 * four lines apart in it, and a copy-paste that swaps them produces a client
 * that works while bypassing every RLS policy in the schema.
 *
 * NO COMPONENT CALLS `.from()`. Every query on this site goes through a named
 * function in `@avexa/api` with this client as its first argument.
 *
 * ─── WHY THIS IS ASYNC, AND WHY THE IMPORT IS A DEEP ONE ────────────────────
 *
 * `@supabase/supabase-js` is 57 kB gzipped — a third of the entire 180 kB
 * first-load budget in Docs/03 — and the marketing homepage does not need one
 * byte of it to paint. But `Footer` is inside `RootLayout`, so it renders on
 * EVERY route, and it calls `useCategoryTree`. Route-splitting therefore cannot
 * help: whatever `useCategories` imports is in the eager entry graph on the
 * homepage, and a static `import { createAvexaClient } from '@avexa/api'` here
 * put the whole client there with it. `index.html` modulepreloaded it.
 *
 * So the factory is fetched on first use instead. Two details make that work,
 * and both are load-bearing:
 *
 *   IT MUST BE `@avexa/api/client`, NOT `@avexa/api`. Dynamically importing the
 *   barrel would be worse than useless. `useCategories` and the directory and
 *   business hooks already import named values from the barrel statically, so
 *   the barrel module itself is eager; a dynamic `import('@avexa/api')` on top
 *   of that asks for its whole namespace object, which defeats the tree-shaking
 *   that currently drops `client.ts` — and drags supabase-js back onto the
 *   critical path via the static chunk. `client.ts` is the only module in the
 *   package that imports supabase-js as a VALUE (everything else takes
 *   `PostgrestError` and `SupabaseClient` as types, which are erased), so
 *   importing that one file is what isolates the cost.
 *
 *   IT COSTS NOTHING A USER PERCEIVES. Every caller is inside a TanStack Query
 *   `queryFn` or `mutationFn`, which was always going to await a network round
 *   trip. The module fetch overlaps the work the query was already doing.
 *
 * Created once, lazily: an unconfigured `.env` must fail where a query runs and
 * can be reported in the UI, not at module-import time where it blanks the page.
 */

let client: AvexaClient | null = null

/**
 * The in-flight creation, so that the six queries a profile page fires on mount
 * import the module once between them rather than six times. Cleared if it
 * rejects — a misconfigured environment that is later fixed by a redeploy
 * should not be cached as a permanent failure for the life of the tab.
 */
let pending: Promise<AvexaClient> | null = null

export const isAvexaConfigured = (): boolean =>
  Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY)

const createClient = async (): Promise<AvexaClient> => {
  const url = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

  if (!url || !anonKey) {
    throw new Error(
      'Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env',
    )
  }

  const { createAvexaClient } = await import('@avexa/api/client')

  return createAvexaClient(url, anonKey, {
    // The marketing site is read-only and anonymous. Persisting a session would
    // write a token into localStorage that nothing on this site ever reads.
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    headers: { 'x-avexa-surface': 'web-public' },
  })
}

export const getAvexaClient = async (): Promise<AvexaClient> => {
  if (client) return client

  pending ??= createClient().then(
    (created) => {
      client = created
      pending = null
      return created
    },
    (error: unknown) => {
      pending = null
      throw error
    },
  )

  return pending
}
